import React, { useEffect, useRef } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import WrapperCard from "../../component-lib/Atoms/card/EventEHRWrapperCard";
import Heading from "../../component-lib/Atoms/heading/EventEHRHeading";
import Table from "../../component-lib/Organisms/table/EventEHRTable";
import MenuDropdown from "../../component-lib/Molecules/menuDropdown/MenuDropdown";
import useDeviceType from "../../custom-hooks/DeviceType";
import { TEXT_CONSTANTS } from "../../utils/text-constants/textConstants";
import {
  destructStatusData,
  getSignBackground,
  getSignDotColor,
  getSignTextColor,
  getStatusBackground,
  getStatusDotColor,
  getStatusTextColor,
} from "../../utils/statusConstants";
import { useDispatch, useSelector } from "react-redux";
import { componentKey } from "./unsignedVisitSlice";
import {
  componentKey as dashboardComponentKey,
  setClosePreviewDialog,
} from "../dashboard-screen/dashboardSlice";
import {
  componentKey as patientComponentKey,
  setDataToDischargeForm,
  setNotesData,
  setVisitPreviewDialog,
  setOrderChange,
  setVisitNoteCareFlowData,
  setRefreshPatientList,
  setCloseCreateTriageDrawer,
} from "../patient-screen/patientSlice";
import { getVisitNoteList } from "./unsignedVisitSaga";
import {
  getVisitNoteById,
  patchPatient,
  createTriage,
  deleteVisit,
} from "../patient-screen/patientSaga";
import DischargeFormPreviewModel from "../../utils/discharge-form-preview-model/DischargeFormPreviewModel";
import SaveTriageDialog from "../patient-screen/visit-flow/SaveTriageDialog";
import EventEHRConfirmationDialog from "../../component-lib/Molecules/confimation-dialog/EventEHRConfirmationDialog";
import TooltipForTable from "../../component-lib/Molecules/tooltip/TooltipForTable";
import SelectDropdown from "../../component-lib/Atoms/Select/Select";
import SearchSelectDropdown from "../../component-lib/Atoms/SearchSelectDropdown/EventEHRSearchSelectDropdown";
import { isOffsiteTypeOfCare } from "../../utils/offsite/isOffsite";
import { isNurseTypeOfCare } from "../../utils/careType/isNurseTypeOfCare";
import {
  getLoginUserData,
  getUserPermission,
  getFacilityId,
} from "../../utils/getToken";
import { searchIcon } from "../../assets";

const UnsignedEncounter = () => {
  const { visitNoteListData } = useSelector(
    (state: any) => state[componentKey]
  );
  const { closePreviewForDialog } = useSelector(
    (state: any) => state[dashboardComponentKey]
  );
  const {
    openVisitPreviewDialog,
    visitNoteCareFlowData,
    refreshPatientList,
    closeTriageDrawer,
  } = useSelector((state: any) => state[patientComponentKey]);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userPermissionData: any = getUserPermission();
  const loginUserData: any = getLoginUserData();
  const parsedLoginUserData = JSON.parse(loginUserData);
  const loginUserFacilityId =
    parsedLoginUserData?.customAttributes?.facilityId || getFacilityId();
  // A visit can be deleted only by SuperAdmin/Admin users who also have
  // provider-portal access (isAdminAccess). Everyone else sees it disabled.
  const canDeleteVisit =
    (parsedLoginUserData?.roleNames?.includes("SuperAdmin") ||
      parsedLoginUserData?.roleNames?.includes("Admin")) &&
    parsedLoginUserData?.customAttributes?.isAdminAccess === "true";

  // SuperAdmin/Admin (with provider access) are not facility-specific, so they
  // may view/open any patient's visit note regardless of the selected facility.
  const isFacilityAgnosticAdmin = canDeleteVisit;

  const [pageNumber, setPageNumber] = useState(1);
  const [rowPerPage, setRowPerPage] = useState("10");
  const [openPreviewForm, setOpenPreviewForm] = useState(false);
  const [isSignedVisit, setIsSignedVisit] = useState(false);
  const deviceType = useDeviceType();
  const [status, setStatus] = useState("unsigned");
  const [searchedValue, setSearchValue] = useState<string>("");
  // ADDED HALF MILLISECOND DELAY IN SEARCH USING THIS HOOK
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // ROW-ACTION STATE (mirrors Patient Details / Dashboard visit kebab)
  const [openTriage, setOpenTriage] = useState(false);
  const [triageIds, setTriageIds] = useState({
    visitId: "",
    patientId: "",
    typeOfCare: "",
  });
  const [openDeleteVisitConfirmDialog, setOpenDeleteVisitConfirmDialog] =
    useState(false);
  const [visitToDelete, setVisitToDelete] = useState<any>(null);

  // Open / continue an unsigned note in its care-type documentation flow.
  const openVisitNote = (row: any) => {
    dispatch(setVisitNoteCareFlowData({}));
    dispatch(setNotesData(row));
    dispatch(setOrderChange(true));
    const base = `/patient/${row?.patientId}`;
    const query = `?isAddNewNote=${true}&newNoteStatus=${row?.status}`;
    const care = row?.care1Copy;
    if (isOffsiteTypeOfCare(care)) {
      navigate(
        `${base}/view-offsite-transfers-care-visit-note/${row?.visitId}${query}`
      );
    } else if (care === "Self_Care") {
      navigate(`${base}/view-self-care-visit-note/${row?.visitId}${query}`);
    } else if (isNurseTypeOfCare(care)) {
      navigate(`${base}/view-nurse-care-visit-note/${row?.visitId}${query}`);
    } else if (care?.toLowerCase()?.includes("mental")) {
      navigate(
        `${base}/view-mental-health-care-visit-note/${row?.visitId}${query}`
      );
    } else if (care?.toLowerCase()?.includes("ear")) {
      navigate(
        `${base}/view-listening-ear-care-visit-note/${row?.visitId}${query}`
      );
    } else {
      navigate(
        `${base}/view-physician-care-visit-note/${row?.visitId}${query}`
      );
    }
  };

  // CREATE TRIAGE (mirrors Dashboard / PatientDetailsScreen mapping)
  const handleCreateTriageDialog = (formData: any) => {
    const payload = {
      apiPayload: {
        patientId: triageIds?.patientId,
        visitId: triageIds?.visitId,
        temperature: formData?.temperature
          ? Number(formData?.temperature)
          : undefined,
        oxygenSaturation: formData?.oxygenSaturation
          ? Number(formData?.oxygenSaturation)
          : undefined,
        heartRate: formData?.heathRate
          ? Number(formData?.heathRate)
          : undefined,
        respirationRate: formData?.respirationRate
          ? Number(formData?.respirationRate)
          : undefined,
        systolic: formData?.bloodPressure
          ? Number(formData?.bloodPressure)
          : undefined,
        diastolic: formData?.bloodPressure2
          ? Number(formData?.bloodPressure2)
          : undefined,
        woundCare: formData?.care === "woundCare" ? true : false,
        woundlocation:
          formData?.care === "woundCare" ? formData?.careValue : "",
        dehydration: formData?.care === "dehydration" ? true : false,
        dehydrationLevel:
          formData?.care === "dehydration" ? formData?.careValue : "",
        injury: formData?.care === "injury" ? true : false,
        injurylocation: formData?.care === "injury" ? formData?.careValue : "",
        other: formData?.care === "other" ? true : false,
        details: formData?.care === "other" ? formData?.careValue : "",
        triageTakenBy: formData?.triageTakenId,
        location: formData?.triageTakenLocation
          ? formData?.triageTakenLocation === "clinicLocation"
            ? "Clinic Location"
            : "JHC"
          : undefined,
      },
      comesFrom: "dashboard",
    };
    dispatch(createTriage(payload));
  };

  // HANDLE DELETE VISIT
  const handleDeleteVisit = () => {
    if (visitToDelete?.visitId) {
      dispatch(deleteVisit({ visitId: visitToDelete.visitId }));
      setOpenDeleteVisitConfirmDialog(false);
      setVisitToDelete(null);
    }
  };

  // RECENT VISIT COLUMN DATA
  const recentVisitColumns = [
    {
      field: "renderingProvider",
      label: <div>Rendering Provider</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal common-text-ellipse-style">
            {row?.renderingProvider}
          </p>
        );
      },
    },
    {
      field: "patientName",
      label: <div>Patient Name</div>,
      renderLogic: (row: any) => {
        return (
          <p
            className="font-medium text-primary-main cursor-pointer"
            onClick={() => {
              if (row?.patientId) navigate(`/patient/${row?.patientId}`);
            }}
          >
            {row?.patientName}
          </p>
        );
      },
    },

    {
      field: "visitType",
      label: <div>Visit Type</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.visitType}</p>;
      },
    },
    {
      field: "chiefComplaint",
      label: <div>Chief Complaint</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 cursor-pointer common-text-ellipse-style">
            <TooltipForTable label={row?.chiefComplaint} />
          </p>
        );
      },
    },
    {
      field: "status",
      label: <div>Status</div>,
      renderLogic: (row: any) => {
        return (
          <div className="text-neutral-90 font-normal flex justify-start gap-1 items-center">
            <span
              className="font-weight-400 font-size-12px flex gap-1 items-center"
              style={{
                color: getStatusTextColor(row?.status),
                background: getStatusBackground(row?.status),
                borderRadius: "46px",
                padding: "4px 12px",
              }}
            >
              <span
                className="w-2 h-2"
                style={{
                  background: getStatusDotColor(row?.status),
                  borderRadius: "50%",
                }}
              ></span>
              {destructStatusData(row?.status)}
            </span>
          </div>
        );
      },
    },
    {
      field: "Signed/Unsigned",
      label: <div>Signed/Unsigned</div>,
      renderLogic: (row: any) => {
        return (
          <div className="text-neutral-90 font-normal flex justify-start gap-1 items-center">
            <span
              className="font-weight-400 font-size-12px flex gap-1 items-center"
              style={{
                color: getSignTextColor(row?.isVisitSign),
                background: getSignBackground(row?.isVisitSign),
                borderRadius: "46px",
                padding: "4px 12px",
              }}
            >
              <span
                className="w-2 h-2"
                style={{
                  background: getSignDotColor(row?.isVisitSign),
                  borderRadius: "50%",
                }}
              ></span>
              {row?.isVisitSign ? "Signed" : "Unsigned"}
            </span>
          </div>
        );
      },
    },
    {
      field: "action",
      label: <div>{TEXT_CONSTANTS?.ACTION}</div>,
      renderLogic: (row: any) => {
        const options: any = [];

        // Jump to the patient's full details screen.
        // options.push({
        //   label: "View Patient Details",
        //   disabled: !row?.patientId,
        //   onClickCb: () => {
        //     if (row?.patientId) navigate(`/patient/${row?.patientId}`);
        //   },
        // });

        // Terminal (finished) notes open the read-only preview; unsigned/
        // in-progress notes open/continue their care-type documentation flow.
        const isTerminal =
          row?.status === "transferOffProperty" ||
          row?.status === "transferToClinic" ||
          row?.status === "transferToJHC" ||
          row?.status === "discharged";

        if (isTerminal) {
          options.push({
            label: "View Visit Note",
            disabled: !userPermissionData?.visit?.view,
            onClickCb: () => {
              dispatch(setNotesData(row));
              dispatch(setOrderChange(true));
              dispatch(
                getVisitNoteById({
                  id: row?.visitId,
                  comesFrom: "viewVisitNote",
                })
              );
              setIsSignedVisit(true);
            },
          });
        } else {
          options.push({
            label: "View Visit Note",
            // A provider can only open the note once the patient is assigned to
            // them at their facility.
            disabled:
              (!isFacilityAgnosticAdmin &&
                !(row?.isAssigned && row?.facilityId === loginUserFacilityId)) ||
              !userPermissionData?.visit?.view,
            onClickCb: () => openVisitNote(row),
          });
        }

        // Add Vitals / Add Rpt Vitals — same gating as Patient Details:
        // excludes Self Care, listening ear, mental health, and offsite; and
        // off-property terminates the visit except for EMS Scene.
        if (
          row?.status !== "discharged" &&
          (row?.status !== "transferOffProperty" ||
            row?.visitTypeRaw === "Scene")
        ) {
          if (
            row?.care1Copy !== "Self_Care" &&
            !row?.care1Copy?.toLowerCase()?.includes("ear") &&
            !row?.care1Copy?.toLowerCase()?.includes("mental") &&
            !isOffsiteTypeOfCare(row?.care1Copy)
          ) {
            options.push({
              label: row?.triage?.length ? "Add Rpt Vitals" : "Add Vitals",
              onClickCb: () => {
                setTriageIds({
                  visitId: row?.visitId,
                  patientId: row?.patientId,
                  typeOfCare: row?.typeOfCare,
                });
                setOpenTriage(true);
              },
            });
          }
        }

        // Assign to me / Unassign — required so the provider can open the note.
        options.push({
          label: row?.isAssigned ? "Unassign" : "Assign to me",
          disabled: false,
          onClickCb: () => {
            dispatch(
              patchPatient({
                id: row?.patientId,
                apiPayload: {
                  assigenProvider: JSON.stringify(!row?.isAssigned),
                },
              })
            );
          },
        });

        // Delete visit — SuperAdmin/Admin with provider-portal access only.
        options.push({
          label: "Delete",
          disabled: !canDeleteVisit,
          onClickCb: () => {
            setVisitToDelete(row);
            setOpenDeleteVisitConfirmDialog(true);
          },
        });

        return (
          <>
            <div className="flex items-center">
              <MenuDropdown options={options} />
            </div>
          </>
        );
      },
    },
  ];

  // HANDLE SEARCH CHANGE VALUE AND ADDED SOME DELAY
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageNumber(1);

    const value = e.target.value;

    // CLEAR THE PREVIOUS TIMEOUT
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // SET A NEW TIMEOUT TO DELAY THE CALLBACK
    typingTimeoutRef.current = setTimeout(() => {
      setSearchValue(value?.trim());
    }, 500); // ADJUST DELAY AS NEEDED
  };

  // RECENT VISIT ROW DATA
  const recentVisitRowData = visitNoteListData?.visitNotes?.map(
    (data: any) => ({
      // note id
      id: data?.id,
      renderingProvider: `${data?.createdProvider?.firstName || "-"} ${data?.createdProvider?.lastName || "-"}`,
      // patient record id (used for assign + routing), not the participant id
      patientId: data?.patient?.id,
      patientName: `${data?.patient?.participant?.firstName || "-"} ${data?.patient?.participant?.lastName || "-"}`,
      visitType: data?.visit?.type === "WALK_In_Visit" ? "Walk In" : "EMS",
      // Raw visit type ("Scene" = EMS Scene) — keeps off-property EMS visits alive.
      visitTypeRaw: data?.visit?.type,
      status: data?.status || "-",
      visitId: data?.visit?.id,
      chiefComplaint: data?.visit?.chiefComplaints || "-",
      isVisitSign: data?.isVisitSign,
      typeOfCare: data?.visit?.typeOfCare,
      care1Copy: data?.visit?.typeOfCare,
      isAssigned: data?.patient?.isAssigned || false,
      facilityId: data?.patient?.facilityId,
      triage: data?.visit?.triage,
    })
  );

  useEffect(() => {
    if (closePreviewForDialog) {
      setOpenPreviewForm(false);
      getVisitNote();
      dispatch(setClosePreviewDialog(false));
    }
  }, [closePreviewForDialog]);

  // OPEN VIEW PREV  DIALOG
  useEffect(() => {
    if (openVisitPreviewDialog) {
      setOpenPreviewForm(true);
      dispatch(setVisitPreviewDialog(false));
      dispatch(
        setDataToDischargeForm({
          ...visitNoteCareFlowData,
        })
      );
    }
  }, [openVisitPreviewDialog]);

  // Refresh after an assign/unassign toggle or a delete (both set this flag)
  // so the list + labels reflect the new state.
  useEffect(() => {
    if (refreshPatientList) {
      dispatch(setRefreshPatientList(false));
      getVisitNote();
    }
  }, [refreshPatientList]);

  // Close the triage dialog and refetch after vitals are saved.
  useEffect(() => {
    if (closeTriageDrawer) {
      setOpenTriage(false);
      dispatch(setCloseCreateTriageDrawer(false));
      getVisitNote();
    }
  }, [closeTriageDrawer]);

  useEffect(() => {
    getVisitNote();
  }, [rowPerPage, pageNumber, status, searchedValue]);

  const getVisitNote = () => {
    if (searchedValue.trim()?.length) {
      const payload = {
        pageNumber: pageNumber,
        limit: rowPerPage,
        searchKey: searchedValue,
        status: status !== "all" ? status : "",
      };
      dispatch(getVisitNoteList(payload));
    } else {
      const payload = {
        pageNumber: pageNumber,
        searchKey: searchedValue,
        limit: rowPerPage,
        status: status !== "all" ? status : "",
      };
      dispatch(getVisitNoteList(payload));
    }
  };
  return (
    <WrapperCard
      customClasses="m-3 p-3 flex-1 min-h-0"
      customStyle={{ minWidth: deviceType !== "mobile" ? "400px" : "" }}
    >
      <div className="flex flex-col h-full gap-3">
        <div
          className={`flex ${deviceType === "mobile" ? "flex-col items-start gap-3" : "items-center"} justify-between w-full`}
        >
          <Heading
            title={"Unsigned Visits"}
            customStyle={{ color: "#1D1D1D" }}
          />
          <div
            className={`flex ${deviceType === "mobile" ? "w-full justify-between" : ""} gap-3`}
          >
            <div>
              <SearchSelectDropdown
                freeSolo={true}
                searchedValue={searchedValue}
                onSearchValueChangeCb={(e) => handleChange(e)}
                size="small"
                label="Search By Patient Name"
                fullWidth={deviceType === "mobile" ? "160px" : "300px"}
                startIcon={
                  <img
                    alt="search-icon"
                    src={searchIcon}
                    width={"18px"}
                    height={"18px"}
                  />
                }
              />
            </div>

            <div style={{ maxWidth: "350px" }}>
              <SelectDropdown
                label=""
                name="status"
                value={status}
                options={[
                  { label: "All", value: "all" },
                  { label: "Signed", value: "signed" },
                  { label: "Unsigned", value: "unsigned" },
                ]}
                onChangeCb={(e) => {
                  setPageNumber(1);
                  setStatus(e.target.value);
                }}
              />
            </div>
          </div>
        </div>
        <div style={{ overflow: "auto" }}>
          <Table
            columns={recentVisitColumns}
            rows={recentVisitRowData}
            noDataFoundMessage={"No recent visit found"}
            paginationProps={{
              isPagination: true,
              page: pageNumber,
              totalPages: visitNoteListData?.totalPages,
              totalCount: visitNoteListData?.total,
              limit: rowPerPage,
              rowsPerPageOptions: [10, 25, 50],
              onPageChange: (page: any) => {
                setPageNumber(page);
              },
              handleChangeRowsPerPage: (rowPerPage: any) => {
                setRowPerPage(rowPerPage);
                setPageNumber(1);
              },
            }}
          />
        </div>
      </div>
      {openPreviewForm && (
        <DischargeFormPreviewModel
          comesFrom={"viewVisitNote"}
          signVisit={isSignedVisit}
          patientId={recentVisitRowData?.[0]?.id}
          visitNoteId={""}
          open={openPreviewForm}
          handleCloseModel={() => {
            setOpenPreviewForm(false);
            setIsSignedVisit(false);
          }}
        />
      )}
      {/* ADD VITALS DIALOG */}
      {openTriage && (
        <SaveTriageDialog
          ids={triageIds}
          open={openTriage}
          handleCloseDialog={() => setOpenTriage(false)}
          handleTriageSubmittedData={handleCreateTriageDialog}
        />
      )}
      {/* DELETE VISIT CONFIRMATION DIALOG */}
      <EventEHRConfirmationDialog
        alertTitle="Delete Visit"
        alertDescription="Are you sure you want to delete this visit? This permanently removes the visit and all of its related data (vitals, visit notes, orders, dispositions). This action cannot be undone."
        cancelButtonTitle="Cancel"
        submitButtonTitle="Delete"
        open={openDeleteVisitConfirmDialog}
        handleClose={(action: any) => {
          if (action === "close") {
            setOpenDeleteVisitConfirmDialog(false);
            setVisitToDelete(null);
          } else if (action === "submit") {
            handleDeleteVisit();
          }
        }}
      />
    </WrapperCard>
  );
};

export default UnsignedEncounter;
